import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

interface LayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  { href: '/dashboard', icon: '🏠', label: 'ダッシュボード' },
  { href: '/', icon: '💬', label: '新規ヒアリング' },
  { href: '/sessions', icon: '📋', label: 'セッション一覧' },
  { href: '/blueprints', icon: '📄', label: '設計書一覧' },
];

const adminMenuItems = [
  { href: '/admin/users', icon: '👥', label: 'ユーザー管理' },
  { href: '/admin/contacts', icon: '📇', label: '知人リスト' },
];

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (href: string) => location.pathname === href;

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background-main)' }}>
      {/* サイドバー */}
      <aside
        className={`${isSidebarOpen ? 'w-[200px]' : 'w-[60px]'} h-screen fixed left-0 top-0 flex flex-col transition-all duration-300 z-40`}
        style={{
          backgroundColor: 'var(--background-aside-primary)',
          borderRight: 'var(--border-aside)',
        }}
      >
        {/* ロゴ */}
        <div className="p-4 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 rounded-full bg-jarvis-gold flex items-center justify-center flex-shrink-0">
            <span className="text-jarvis-navy-dark font-bold text-xl">J</span>
          </div>
          {isSidebarOpen && (
            <div className="overflow-hidden">
              <h1 className="font-bold text-lg" style={{ color: 'var(--aside-text-color)' }}>
                Jarvis
              </h1>
            </div>
          )}
        </div>

        {/* メニュー */}
        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="space-y-1 px-2">
            {menuItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(item.href) ? 'bg-white/20' : 'hover:bg-white/10'
                }`}
                style={{ color: 'var(--aside-text-color)' }}
              >
                <span className="text-lg">{item.icon}</span>
                {isSidebarOpen && <span className="text-sm">{item.label}</span>}
              </Link>
            ))}
          </div>

          {/* 管理者メニュー */}
          {user?.role === 'admin' && (
            <>
              <div className="my-4 mx-4 border-t border-white/10" />
              <div className="px-4 mb-2">
                {isSidebarOpen && (
                  <span className="text-xs uppercase tracking-wider" style={{ color: 'var(--aside-text-color)', opacity: 0.5 }}>
                    管理
                  </span>
                )}
              </div>
              <div className="space-y-1 px-2">
                {adminMenuItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive(item.href) ? 'bg-white/20' : 'hover:bg-white/10'
                    }`}
                    style={{ color: 'var(--aside-text-color)' }}
                  >
                    <span className="text-lg">{item.icon}</span>
                    {isSidebarOpen && <span className="text-sm">{item.label}</span>}
                  </Link>
                ))}
              </div>
            </>
          )}
        </nav>

        {/* サイドバートグル */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-4 border-t border-white/10 hover:bg-white/10 transition-colors"
          style={{ color: 'var(--aside-text-color)' }}
        >
          {isSidebarOpen ? '◀' : '▶'}
        </button>
      </aside>

      {/* メインコンテンツ */}
      <div className={`flex-1 ${isSidebarOpen ? 'ml-[200px]' : 'ml-[60px]'} transition-all duration-300`}>
        {/* ヘッダー */}
        <header
          className="h-16 sticky top-0 z-30 flex items-center justify-between px-6 border-b"
          style={{
            backgroundColor: 'var(--background-card)',
            borderColor: 'var(--border-color)',
          }}
        >
          {/* 左側: ページタイトル */}
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {menuItems.find(m => m.href === location.pathname)?.label ||
               adminMenuItems.find(m => m.href === location.pathname)?.label ||
               'Jarvis'}
            </h2>
          </div>

          {/* 右側: テーマ切替 & プロフィール */}
          <div className="flex items-center gap-4">
            {/* テーマ切替 */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={theme === 'light' ? 'ダークモードに切り替え' : 'ライトモードに切り替え'}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>

            {/* プロフィールメニュー */}
            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-jarvis-gold text-jarvis-navy-dark flex items-center justify-center font-bold">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
              </button>

              {isProfileOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg border py-2 z-50"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                  }}
                >
                  {/* ユーザー情報 */}
                  <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <p className="font-medium" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{user?.email}</p>
                    {user?.isMaster && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-jarvis-gold text-jarvis-navy-dark font-medium">
                        MASTER
                      </span>
                    )}
                  </div>

                  {/* メニュー項目 */}
                  <Link
                    to="/profile"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    👤 プロフィール変更
                  </Link>
                  <Link
                    to="/change-password"
                    onClick={() => setIsProfileOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    🔑 パスワード変更
                  </Link>

                  <div className="my-2 border-t" style={{ borderColor: 'var(--border-color)' }} />

                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-red-500"
                  >
                    🚪 ログアウト
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* コンテンツ */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

